import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../../lib/useApi.js';
import { useClientPagination } from '../../lib/usePagination.js';
import {
  PageHeader,
  ErrorState,
  EmptyState,
  StatusChip,
  PageViewerControls,
  KpiHeroBand,
  deriveKpiTone,
  StatusDonutMini,
  InlineScoreCell,
  bandTone,
  MissionImpactHeatmap,
  KpiBandSkeleton,
  ChartSkeleton,
  TableSkeleton,
  BrandedEmptyState,
  StaggerReveal,
} from '../../components/index.js';
import './marketResearch.css';
import './marketResearch.redesign.css';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'recommended', label: 'Recommended' },
  { id: 'enterprise', label: 'Enterprise reuse' },
  { id: 'custom', label: 'Custom/internal' },
  { id: 'new-vendor', label: 'New vendor' },
  { id: 'active-status', label: 'Active evaluation' },
  { id: 'watch-status', label: 'Watchlist/baseline' },
  { id: 'watch', label: 'Watch/defer/no-go' },
];

export function MarketResearchTrackerPage() {
  const tools = useApi('/market-research/tools', { query: { limit: 100 } });
  const candidates = useApi('/market-research/candidates', { query: { limit: 100 } });
  const summary = useApi('/market-research/summary');
  const matrix = useApi('/market-research/capability-matrix');
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);

  const loading = tools.loading || candidates.loading || summary.loading || matrix.loading;
  const error = tools.error || candidates.error || summary.error || matrix.error;
  const capabilityRows = useMemo(() => tools.data?.data || [], [tools.data]);
  const candidateRows = useMemo(() => candidates.data?.data || [], [candidates.data]);
  const summaryData = summary.data?.data || {};
  const matrixData = matrix.data?.data || { capabilities: [], rows: [] };

  const sortedCandidates = useMemo(
    () =>
      [...candidateRows].sort(
        (a, b) =>
          recommendationRank(a.recommendation) - recommendationRank(b.recommendation) ||
          Number(b.capabilityFitScore || 0) - Number(a.capabilityFitScore || 0),
      ),
    [candidateRows],
  );

  const filteredCandidates = useMemo(
    () => sortedCandidates.filter((candidate) => matchesFilter(candidate, activeFilter)),
    [activeFilter, sortedCandidates],
  );
  const candidatePager = useClientPagination(filteredCandidates, {
    initialPageSize: 8,
    resetKey: activeFilter,
  });

  const selectedCandidate =
    filteredCandidates.find((candidate) => candidate.id === selectedId) ||
    filteredCandidates[0] ||
    sortedCandidates[0];

  // Recommendation mix across the full evaluated set (not the filtered view) so
  // the donut always reflects the true portfolio of solution paths.
  const recommendationMix = useMemo(
    () => buildRecommendationMix(sortedCandidates),
    [sortedCandidates],
  );

  if (loading)
    return (
      <div className="market-research-page" data-testid="market-research-dashboard">
        <PageHeader
          eyebrow="Delivery"
          title="Market Research Tracker"
          subtitle="Decision-ready comparison of solution paths, source-system fit, and COA evidence."
        />
        <KpiBandSkeleton count={5} />
        <section className="market-research-layout">
          <div className="market-main-column">
            <section className="card">
              <TableSkeleton rows={6} />
            </section>
            <section className="card market-matrix-card">
              <ChartSkeleton height={220} />
            </section>
          </div>
          <aside className="market-detail-column">
            <section className="card">
              <TableSkeleton rows={5} />
            </section>
          </aside>
        </section>
      </div>
    );
  if (error) return <ErrorState error={error} />;

  return (
    <div className="market-research-page" data-testid="market-research-dashboard">
      <PageHeader
        eyebrow="Delivery"
        title="Market Research Tracker"
        subtitle="Decision-ready comparison of solution paths, source-system fit, and COA evidence."
      />

      <KpiHeroBand
        eyebrow="Market research posture"
        items={buildHeroItems(summaryData.kpis)}
        columns={5}
      />

      <StaggerReveal as="section" className="market-research-layout">
        <div className="market-main-column">
          <section className="card market-shortlist-card">
            <div className="market-section-head">
              <div>
                <div className="eyebrow">Candidate solutions</div>
                <h2 className="card-title">Ranked candidate shortlist</h2>
              </div>
              <span className="chip blue">{filteredCandidates.length} visible</span>
            </div>
            <div className="market-filter-row" aria-label="Market research filters">
              {FILTERS.map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  className={`market-filter ${activeFilter === filter.id ? 'active' : ''}`}
                  onClick={() => setActiveFilter(filter.id)}
                  aria-pressed={activeFilter === filter.id}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            {filteredCandidates.length === 0 ? (
              <EmptyState title="No candidates match this filter" />
            ) : (
              <div className="market-candidate-list" data-testid="market-candidate-list">
                {candidatePager.items.map((candidate) => (
                  <button
                    key={candidate.id}
                    type="button"
                    className={`market-candidate-row ${
                      selectedCandidate?.id === candidate.id ? 'active' : ''
                    }`}
                    onClick={() => setSelectedId(candidate.id)}
                  >
                    <span className="market-candidate-main">
                      <span className="market-candidate-title">{candidate.name}</span>
                      <span className="market-candidate-summary">{candidate.summary}</span>
                      <span className="market-chip-row">
                        <RecommendationChip recommendation={candidate.recommendation}>
                          {candidate.recommendationLabel ||
                            prettyRecommendation(candidate.recommendation)}
                        </RecommendationChip>
                        <span className="chip outline">{pretty(candidate.candidateType)}</span>
                        <span className="chip outline">
                          {pretty(candidate.integrationReadiness)} readiness
                        </span>
                        <span className="chip outline">
                          {pretty(candidate.estimatedCostBand)} cost
                        </span>
                      </span>
                    </span>
                    <span className="market-score-rail" aria-label="Capability fit score">
                      <span className="eyebrow">Fit</span>
                      {candidate.capabilityFitScore == null ? (
                        <strong className="mr-tone-slate">--</strong>
                      ) : (
                        <InlineScoreCell
                          value={candidate.capabilityFitScore}
                          max={100}
                          suffix="%"
                          ariaLabel={`Capability fit ${candidate.capabilityFitScore} percent`}
                        />
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}
            <PageViewerControls
              page={candidatePager.page}
              label="candidates"
              pageSize={candidatePager.pageSize}
              onOffsetChange={candidatePager.setOffset}
              onPageSizeChange={candidatePager.setPageSize}
            />
          </section>

          <section className="card market-matrix-card">
            <div className="market-section-head">
              <div>
                <div className="eyebrow">Capability evidence</div>
                <h2 className="card-title">Capability fit matrix</h2>
              </div>
              <span className="muted small">{capabilityRows.length} capabilities tracked</span>
            </div>
            <CapabilityMatrix matrix={matrixData} />
          </section>
        </div>

        <aside className="market-detail-column">
          <RecommendationMixCard mix={recommendationMix} />
          <CandidateProfile candidate={selectedCandidate} />
          <DecisionFindings findings={summaryData.findings} />
        </aside>
      </StaggerReveal>
    </div>
  );
}

// Recommendation-mix rollup donut. Tones bind to the executive-traffic-light
// scale: advance -> sage, watch -> amber, defer -> slate, no-go -> brick.
const RECOMMENDATION_TONE = {
  advance: 'sage',
  watch: 'amber',
  defer: 'slate',
  'no-go': 'brick',
};

function buildRecommendationMix(candidates = []) {
  const order = ['advance', 'watch', 'defer', 'no-go'];
  const counts = candidates.reduce((acc, candidate) => {
    const key = candidate.recommendation || 'unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  return order
    .filter((key) => counts[key])
    .map((key) => ({
      label: prettyRecommendation(key),
      count: counts[key],
      tone: RECOMMENDATION_TONE[key] || 'slate',
    }));
}

function RecommendationMixCard({ mix = [] }) {
  const total = mix.reduce((sum, segment) => sum + segment.count, 0);
  if (!total) return null;
  const advancing = mix.find((segment) => segment.tone === 'sage')?.count || 0;
  return (
    <section className="card market-mix-card">
      <div className="eyebrow">Decision posture</div>
      <h2 className="card-title">Recommendation mix</h2>
      <div className="market-mix-body">
        <StatusDonutMini
          segments={mix}
          size={132}
          totalLabel="paths"
          ariaLabel="Recommendation mix across evaluated solution paths"
        />
        <p className="market-mix-note">
          {advancing} of {total} evaluated solution path{total === 1 ? '' : 's'}{' '}
          {advancing === 1 ? 'is' : 'are'} recommended to advance to pilot. The rest are held on the
          watch, defer, or no-go track pending governance and integration evidence.
        </p>
      </div>
    </section>
  );
}

// Map the summary KPI block to KpiHeroBand items, deriving each tile's tone from
// the executive-traffic-light rule rather than decorative color.
function buildHeroItems(kpis = {}) {
  const advance = kpis.recommendedToAdvance ?? null;
  const openGaps = kpis.openGaps ?? null;
  const avgFit = kpis.averageCapabilityFit ?? null;
  return [
    {
      id: 'candidates-evaluated',
      label: 'Candidates evaluated',
      value: kpis.candidatesEvaluated ?? '--',
      footer: 'Solution paths',
      icon: '◎',
      status: 'blue',
    },
    {
      id: 'recommended-advance',
      label: 'Recommended to advance',
      value: advance ?? '--',
      footer: 'Pilot-ready',
      icon: '▲',
      status: advance ? 'sage' : 'slate',
    },
    {
      id: 'enterprise-reuse',
      label: 'Enterprise reuse',
      value: kpis.enterpriseReuseOptions ?? '--',
      footer: 'Existing investments',
      icon: '↻',
      status: 'slate',
    },
    {
      id: 'open-gaps',
      label: 'Open gaps',
      value: openGaps ?? '--',
      footer: 'Need closure',
      icon: '!',
      status: openGaps == null ? 'slate' : openGaps > 0 ? 'amber' : 'sage',
    },
    {
      id: 'average-fit',
      label: 'Average fit',
      value: avgFit != null ? avgFit : '--',
      unit: avgFit != null ? '%' : undefined,
      footer: 'Capability score',
      icon: '◆',
      status: avgFit == null ? 'slate' : deriveKpiTone({ value: avgFit, target: 80 }),
    },
  ];
}

function CandidateProfile({ candidate }) {
  if (!candidate) {
    return (
      <section className="card" data-testid="market-research-profile">
        <EmptyState title="No candidate selected" />
      </section>
    );
  }
  return (
    <section className="card market-profile" data-testid="market-research-profile">
      <div className="market-section-head">
        <div>
          <div className="eyebrow">Selected option</div>
          <h2 className="card-title">Candidate profile</h2>
        </div>
        <StatusChip status={candidate.status} />
      </div>
      <h3>{candidate.name}</h3>
      <p>{candidate.recommendationRationale || candidate.summary}</p>
      <div className="market-profile-grid">
        <Metric label="Fit" value={`${candidate.capabilityFitScore ?? '--'}%`} />
        <Metric label="Readiness" value={pretty(candidate.integrationReadiness)} />
        <Metric label="Cost" value={pretty(candidate.estimatedCostBand)} />
        <Metric label="Pilot" value={pretty(candidate.estimatedPilotComplexity)} />
      </div>
      <div className="market-callout">
        <span className="eyebrow">Next action</span>
        <strong>{candidate.nextAction || 'No next action recorded.'}</strong>
      </div>
      <LinkedCoas ids={candidate.relatedCoaIds} />
      <EvidenceList title="Evidence" items={candidate.evidence} />
      <TwoColumnList
        leftTitle="Pros"
        leftItems={candidate.pros}
        rightTitle="Gaps"
        rightItems={candidate.gaps}
      />
      <TwoColumnList
        leftTitle="Cons"
        leftItems={candidate.cons}
        rightTitle="Risks"
        rightItems={candidate.riskFlags}
      />
    </section>
  );
}

function DecisionFindings({ findings = [] }) {
  const pager = useClientPagination(findings || [], {
    initialPageSize: 5,
    resetKey: `findings-${findings?.length || 0}`,
  });
  return (
    <section className="card market-findings">
      <div className="eyebrow">Executive readout</div>
      <h2 className="card-title">Decision board findings</h2>
      {pager.items.map((finding, index) => (
        <div key={finding.id || finding.title || index} className="market-finding">
          <strong>{finding.title}</strong>
          <p>{finding.summary}</p>
        </div>
      ))}
      <PageViewerControls
        page={pager.page}
        label="findings"
        pageSize={pager.pageSize}
        onOffsetChange={pager.setOffset}
        onPageSizeChange={pager.setPageSize}
      />
    </section>
  );
}

// fitBand -> executive-traffic-light tone. Falls back to the numeric band rule
// when an assessment has no explicit band.
function fitBandTone(band, score) {
  const b = String(band || '').toLowerCase();
  if (b === 'strong') return 'sage';
  if (b === 'partial') return 'amber';
  if (b === 'gap') return 'brick';
  return bandTone(score, { good: 80, warn: 50 });
}

// Transpose the candidate-by-capability matrix into a MissionImpactHeatmap:
// rows = capabilities, columns = candidate options, cells = real fitScore + tone.
// Header short labels + definitions come from the server (capabilityMetaByKey and
// per-row shortLabel/fullLabel/whatIs/dataTrust); prettyCapability() is only a
// fallback for keys the server did not annotate.
function buildHeatmapModel(matrix) {
  const capabilities = matrix.capabilities || [];
  const candidateRows = matrix.rows || [];
  const capabilityMeta = matrix.capabilityMetaByKey || {};
  const scaleNote = matrix.marketPolicy?.scaleNote || null;

  const capShort = (capability) =>
    capabilityMeta[capability]?.shortLabel || prettyCapability(capability);

  const rows = capabilities.map((capability) => {
    const meta = capabilityMeta[capability] || {};
    const fullLabel = meta.fullLabel || prettyCapability(capability);
    return {
      portfolioId: capability,
      label: meta.shortLabel || fullLabel,
      fullLabel,
      whatIs: meta.whatIs || null,
      scaleNote,
    };
  });
  const columns = candidateRows.map((row) => {
    const fullLabel = row.fullLabel || row.candidateName;
    return {
      key: row.candidateId,
      label: row.shortLabel || fullLabel,
      fullLabel,
      whatIs: row.whatIs || null,
      scaleNote,
      trust: row.dataTrust || null,
      tone:
        row.capabilityFitScore != null
          ? bandTone(row.capabilityFitScore, { good: 80, warn: 50 })
          : 'slate',
    };
  });

  const cells = [];
  for (const row of candidateRows) {
    const candidateLabel = row.shortLabel || row.candidateName;
    for (const capability of capabilities) {
      const score = row.scores?.[capability];
      if (!score || score.fitScore == null) continue;
      const tone = fitBandTone(score.fitBand, score.fitScore);
      const capLabel = capShort(capability);
      const drivers = [];
      if (score.evidenceSummary)
        drivers.push({ label: 'Evidence', value: score.evidenceSummary, tone });
      if (score.gapNotes)
        drivers.push({ label: 'Gap notes', value: score.gapNotes, tone: 'amber' });
      cells.push({
        portfolioId: capability,
        dimensionKey: row.candidateId,
        portfolioLabel: capLabel,
        dimensionLabel: candidateLabel,
        score: score.fitScore,
        tone,
        summary: score.evidenceSummary || `${capLabel} fit for ${candidateLabel}.`,
        drivers,
      });
    }
  }
  return { rows, columns, cells };
}

function CapabilityMatrix({ matrix }) {
  const model = useMemo(() => buildHeatmapModel(matrix), [matrix]);
  if (!model.rows.length || !model.columns.length || !model.cells.length) {
    return (
      <BrandedEmptyState
        tone="slate"
        icon="▦"
        eyebrow="Capability evidence"
        title="No capability fit assessments yet"
        description="Capability-by-candidate fit scores will render here as a mission-impact heatmap once assessments are recorded for the evaluated solution paths."
      />
    );
  }
  return (
    <div className="market-matrix-host">
      <MissionImpactHeatmap
        rows={model.rows}
        columns={model.columns}
        cells={model.cells}
        cellSize={56}
        gap={12}
        ariaLabel="Capability fit heatmap — capabilities by candidate solution path"
      />
      <ul className="market-matrix-legend" aria-label="Fit score legend">
        <li>
          <span className="mr-swatch sage" aria-hidden /> Strong fit
        </li>
        <li>
          <span className="mr-swatch amber" aria-hidden /> Partial fit
        </li>
        <li>
          <span className="mr-swatch brick" aria-hidden /> Gap
        </li>
      </ul>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="market-profile-metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function LinkedCoas({ ids = [] }) {
  if (!ids?.length) return null;
  return (
    <div className="market-linked-coas">
      <span className="eyebrow">Linked COAs</span>
      <div className="market-chip-row">
        {ids.map((id) => (
          <Link key={id} to="/decision-support" className="chip blue">
            {id}
          </Link>
        ))}
      </div>
    </div>
  );
}

function EvidenceList({ title, items = [] }) {
  if (!items?.length) return null;
  return (
    <div className="market-list-block">
      <strong>{title}</strong>
      <ul>
        {items.map((item, index) => (
          <li key={item.id || item.label || index}>
            <span>{item.label || item.title}</span>
            {item.detail ? <small>{item.detail}</small> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function TwoColumnList({ leftTitle, leftItems = [], rightTitle, rightItems = [] }) {
  return (
    <div className="market-two-col">
      <TextList title={leftTitle} items={leftItems} />
      <TextList title={rightTitle} items={rightItems} />
    </div>
  );
}

function TextList({ title, items = [] }) {
  const normalized = (items || []).map((item) => (typeof item === 'string' ? item : item.label));
  if (!normalized.length) return null;
  return (
    <div className="market-list-block">
      <strong>{title}</strong>
      <ul>
        {normalized.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function RecommendationChip({ recommendation, children }) {
  const tone =
    recommendation === 'advance' ? 'green' : recommendation === 'watch' ? 'amber' : 'red';
  return <span className={`chip ${tone}`}>{children}</span>;
}

function matchesFilter(candidate, filter) {
  if (filter === 'recommended') return candidate.recommendation === 'advance';
  if (filter === 'enterprise') return candidate.existingEnterpriseInvestment;
  if (filter === 'custom')
    return ['custom-build', 'hybrid', 'internal-tool'].includes(candidate.candidateType);
  if (filter === 'new-vendor') return candidate.candidateType === 'new-vendor';
  if (filter === 'active-status') return candidate.status === 'active-evaluation';
  if (filter === 'watch-status') return ['watchlist', 'baseline'].includes(candidate.status);
  if (filter === 'watch') return ['watch', 'defer', 'no-go'].includes(candidate.recommendation);
  return true;
}

function recommendationRank(recommendation) {
  if (recommendation === 'advance') return 0;
  if (recommendation === 'watch') return 1;
  if (recommendation === 'defer') return 2;
  if (recommendation === 'no-go') return 3;
  return 4;
}

function pretty(value) {
  if (!value) return '--';
  return String(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
}

function prettyCapability(value) {
  return pretty(value)
    .replace(/\bCoa\b/g, 'COA')
    .replace(/\bAi\b/g, 'AI')
    .replace(/\bPm\b/g, 'PM');
}

function prettyRecommendation(value) {
  if (value === 'advance') return 'Advance';
  if (value === 'no-go') return 'No-go';
  return pretty(value);
}
